/**
 * ClientRegistrationPage — On-Premises / Hybrid / Cloud-Migration Client Registration
 * Features: 4-step wizard, industry column, column-order persistence, connection dots, lead count badge
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ERPAdvancedGrid from '../components/erp/ERPAdvancedGrid';
import { Settings, X, Eye, EyeOff, CheckCircle, XCircle, Wifi, WifiOff, Loader,
  ChevronRight, ChevronLeft, Server, Database, Link2, BarChart2, ExternalLink,
  Lock, Unlock, Plus, Copy, RefreshCw, Trash2 } from 'lucide-react';
import { apiFetch, API } from '../lib/api';
import '../styles/erp-theme.css';

// ── Column definitions ─────────────────────────────────────────────────────────
const LEAD_COLS_KEY   = 'cliq_deleted_lead_cols';
const CLIENT_COLS_KEY = 'cliq_deleted_client_cols';
function getDeletedCols(key) {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); } catch { return new Set(); }
}
function saveDeletedCols(key, set) {
  localStorage.setItem(key, JSON.stringify([...set]));
}

const ALL_LEAD_COLUMNS = [
  { field: 'companyName',       header: 'Company Name',   frozen: true,  visible: true  },
  { field: 'alreadyRegistered', header: 'Action',         frozen: false, visible: true  },  // ← 2nd col
  { field: 'industryCode',      header: 'Industry',       frozen: false, visible: true  },
  { field: 'contactName',       header: 'Contact Person', frozen: false, visible: true  },
  { field: 'phone',             header: 'Mobile',         frozen: false, visible: true  },
  { field: 'email',             header: 'Email',          frozen: false, visible: true  },
  { field: 'country',           header: 'Country',        frozen: false, visible: true  },
  { field: 'city',              header: 'City',           frozen: false, visible: true  },
  { field: 'gst',               header: 'GSTIN',          frozen: false, visible: true  },
  { field: 'pan',               header: 'PAN',            frozen: false, visible: true  },
  { field: 'expiryDate',        header: 'Expiry Date',    frozen: false, visible: true  },
  { field: 'activationDate',    header: 'Activation',     frozen: false, visible: true  },
  { field: 'ActivationNo',      header: 'Activation No',  frozen: false, visible: true  },
  { field: 'NameoftheEngineer', header: 'Engineer',       frozen: false, visible: true  },
  { field: 'ContactNo',         header: 'Contact No',     frozen: false, visible: false },
  { field: 'TotalAmt',          header: 'Total Amt',      frozen: false, visible: false },
  { field: 'AdvanceReceived',   header: 'Advance Recd',   frozen: false, visible: false },
  { field: 'ReceivedAmt',       header: 'Received',       frozen: false, visible: false },
  { field: 'BalanceAmt',        header: 'Balance',        frozen: false, visible: false },
  { field: 'CompanyName',       header: 'TCM Company',    frozen: false, visible: false },
  { field: 'ContactPerson',     header: 'TCM Contact',    frozen: false, visible: false },
  { field: 'CustContactNo',     header: 'Cust Phone',     frozen: false, visible: false },
  { field: 'EmailAddress',      header: 'Cust Email',     frozen: false, visible: false },
  { field: 'TinNo',             header: 'TIN No',         frozen: false, visible: false },
  { field: 'OfficeNo',          header: 'Office No',      frozen: false, visible: false },
  { field: 'Address',           header: 'Address',        frozen: false, visible: false },
  { field: 'Status',            header: 'TCM Status',     frozen: false, visible: false },
  { field: 'BranchId',          header: 'Branch ID',      frozen: false, visible: false },
  { field: 'CompId',            header: 'Comp ID',        frozen: false, visible: false },
  { field: 'UserId',            header: 'User ID',        frozen: false, visible: false },
  { field: 'S_Expiry_date',     header: 'S Expiry',       frozen: false, visible: false },
  { field: 'S_Create_date',     header: 'S Created',      frozen: false, visible: false },
  { field: 'S_modify_date',     header: 'S Modified',     frozen: false, visible: false },
  { field: 'createdDate',       header: 'Lead Date',      frozen: false, visible: false },
];

const ALL_CLIENT_COLUMNS = [
  { field: 'ClientId',         header: 'Client ID',    frozen: true,  visible: true  },
  { field: 'CompanyName',      header: 'Company',      frozen: true,  visible: true  },
  { field: 'ClientType',       header: 'Type',         frozen: false, visible: true  },
  { field: 'ServiceType',      header: 'Service',      frozen: false, visible: true  },
  { field: 'mbiRegister',      header: 'Action',       frozen: false, visible: true  },
  { field: 'EmailId',          header: 'Email',        frozen: false, visible: true  },
  { field: 'PhoneNo1',         header: 'Phone',        frozen: false, visible: true  },
  { field: 'Country',          header: 'Country',      frozen: false, visible: true  },
  { field: 'Currency',         header: 'Currency',     frozen: false, visible: true  },
  { field: 'City',             header: 'City',         frozen: false, visible: true  },
  { field: 'State',            header: 'State',        frozen: false, visible: false },
  { field: 'Address1',         header: 'Address',      frozen: false, visible: false },
  { field: 'GSTIN',            header: 'GSTIN',        frozen: false, visible: true  },
  { field: 'PANNo',            header: 'PAN',          frozen: false, visible: false },
  { field: 'WebSite',          header: 'Website',      frozen: false, visible: false },
  { field: 'OnPremSQLServer',  header: 'SQL Server',   frozen: false, visible: true  },
  { field: 'OnPremSQLDB',      header: 'SQL DB',       frozen: false, visible: true  },
  { field: 'OnPremSQLUser',    header: 'SQL User',     frozen: false, visible: false },
  { field: 'CloudSQLServer',   header: 'Cloud Server', frozen: false, visible: false },
  { field: 'CloudSQLDB',       header: 'Cloud DB',     frozen: false, visible: false },
  { field: 'LicenseId',        header: 'License ID',   frozen: false, visible: true  },
  { field: 'RegSource',        header: 'Reg Source',   frozen: false, visible: true  },
  { field: 'sqlStatus',        header: 'SQL Ping',     frozen: false, visible: true  },
  { field: 'Status',           header: 'Status',       frozen: false, visible: true  },
  { field: 'CreatedAt',        header: 'Registered',   frozen: false, visible: true  },
];

const CLIENT_TYPES = [
  { id:'ONPREMISES',      label:'On-Premises',     desc:'ERP runs entirely on the client\'s own server/network. You will configure the SQL Server IP and credentials for license validation and remote support.' },
  { id:'HYBRID',          label:'Hybrid',           desc:'Client has existing on-premises ERP but is partially migrating to cloud. Configure both the on-prem SQL Server and the cloud target details.' },
  { id:'CLOUD_MIGRATION', label:'Cloud Migration',  desc:'ERP is being fully moved from on-premises to cloud hosting. Provide the current on-prem server (source) and cloud destination server details.' },
  { id:'SAAS',            label:'SaaS (Managed)',   desc:'Client uses RetailCLIQ cloud-hosted ERP. No SQL Server configuration needed — the platform manages everything automatically.' },
];

const SERVICE_TYPES = [{ id:'ERP',label:'ERP' },{ id:'BI',label:'BI' },{ id:'ERP+BI',label:'ERP + BI' }];

const GST_STATE_MAP = {
  '01':'Jammu & Kashmir','02':'Himachal Pradesh','03':'Punjab','04':'Chandigarh',
  '05':'Uttarakhand','06':'Haryana','07':'Delhi','08':'Rajasthan','09':'Uttar Pradesh',
  '10':'Bihar','11':'Sikkim','12':'Arunachal Pradesh','13':'Nagaland','14':'Manipur',
  '15':'Mizoram','16':'Tripura','17':'Meghalaya','18':'Assam','19':'West Bengal',
  '20':'Jharkhand','21':'Odisha','22':'Chhattisgarh','23':'Madhya Pradesh','24':'Gujarat',
  '25':'Daman & Diu','26':'Dadra & Nagar Haveli','27':'Maharashtra','28':'Andhra Pradesh',
  '29':'Karnataka','30':'Goa','31':'Lakshadweep','32':'Kerala','33':'Tamil Nadu',
  '34':'Puducherry','35':'Andaman & Nicobar','36':'Telangana','37':'Andhra Pradesh (new)',
  '38':'Ladakh','97':'Other Territory','99':'Centre Jurisdiction',
};

function validateGST(gst) {
  if (!gst) return true;
  return /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst.trim().toUpperCase());
}
function fmtDate(v) {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(v)) return v;
  try { return new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); } catch { return String(v); }
}

const inp = { width:'100%',padding:'6px 9px',borderRadius:4,border:'1px solid var(--erp-border)',background:'#fff',color:'var(--erp-text)',fontSize:12,outline:'none',boxSizing:'border-box' };
const inpErr = { ...inp, borderColor:'#dc2626',background:'#fff8f8' };
const lbl = { fontSize:11,color:'var(--erp-text-muted)',fontWeight:600,marginBottom:3,display:'block' };

// ── Lead DB Settings Modal ────────────────────────────────────────────────────
function LeadDBSettingsModal({ onClose }) {
  const [form, setForm] = useState({ server:'',port:'1433',database:'LeadManagement_DEV',user:'',password:'' });
  const [showPass, setSP] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  useEffect(()=>{
    apiFetch(`${API}/client-reg/lead-db-config`).then(r=>r.json())
      .then(d=>{if(d&&!d.error)setForm(f=>({...f,server:d.server||'',port:String(d.port||1433),database:d.database||'LeadManagement_DEV',user:d.user||''}));})
      .catch(()=>{});
  },[]);
  async function testOrSave(testOnly){
    if(!form.server.trim()||!form.database.trim()){setResult({ok:false,error:'Server and Database are required'});return;}
    setBusy(true);setResult(null);
    try{
      const payload={...form,testOnly};if(!form.password)delete payload.password;
      const d=await apiFetch(`${API}/client-reg/lead-db-config`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)}).then(r=>r.json());
      setResult(d);if(!testOnly&&d.ok)setTimeout(onClose,1500);
    }catch(e){setResult({ok:false,error:e.message});}
    finally{setBusy(false);}
  }
  return (
    <div style={{position:'fixed',inset:0,zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:460,background:'var(--erp-surface)',borderRadius:'var(--erp-radius-lg)',border:'1px solid var(--erp-border)',boxShadow:'var(--erp-shadow-2)',overflow:'hidden'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--erp-border)',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fafbfc'}}>
          <div style={{fontSize:13,fontWeight:700,color:'var(--erp-text)',display:'flex',alignItems:'center',gap:6}}><Settings size={14}/> Lead DB Connection Settings</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--erp-text-muted)'}}><X size={14}/></button>
        </div>
        <div style={{padding:16,display:'flex',flexDirection:'column',gap:12}}>
          <div style={{fontSize:11,color:'#1e40af',background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:4,padding:'8px 10px'}}>
            Configure LeadManagement DB connection. Saved to server — takes effect immediately.
          </div>
          {[['server','SQL Server / Host *','text'],['port','Port','number'],['database','Database Name *','text'],['user','SQL Username','text']].map(([k,l,t])=>(
            <div key={k}><label style={lbl}>{l}</label><input data-testid={`ldb-${k}`} type={t} value={form[k]} onChange={e=>set(k,e.target.value)} style={inp}/></div>
          ))}
          <div>
            <label style={lbl}>Password (blank = keep existing)</label>
            <div style={{position:'relative'}}>
              <input data-testid="ldb-password" type={showPass?'text':'password'} value={form.password} onChange={e=>set('password',e.target.value)} style={{...inp,paddingRight:32}} placeholder="Enter new password or leave blank"/>
              <button type="button" onClick={()=>setSP(s=>!s)} style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}>{showPass?<EyeOff size={13}/>:<Eye size={13}/>}</button>
            </div>
          </div>
          {result&&(result.ok
            ?<div style={{fontSize:12,color:'green',background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:4,padding:'7px 10px',display:'flex',alignItems:'center',gap:6}}><CheckCircle size={12}/> Connected — {result.dbName} on {result.serverName}</div>
            :<div style={{fontSize:12,color:'#dc2626',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:4,padding:'7px 10px',display:'flex',alignItems:'center',gap:6}}><XCircle size={12}/> {String(result.error||'Connection failed').slice(0,150)}</div>
          )}
        </div>
        <div style={{padding:'10px 16px',borderTop:'1px solid var(--erp-border)',display:'flex',justifyContent:'flex-end',gap:8,background:'#fafbfc'}}>
          <button onClick={()=>testOrSave(true)} disabled={busy} className="erp-btn" data-testid="ldb-test">{busy?'Testing…':'Test Connection'}</button>
          <button onClick={()=>testOrSave(false)} disabled={busy||!form.server||!form.database} className="erp-btn erp-btn-primary" data-testid="ldb-save">{busy?'Saving…':'Save & Reconnect'}</button>
        </div>
      </div>
    </div>
  );
}

// ── SQL Connection Tester ─────────────────────────────────────────────────────
function ConnectionTester({ server, port, database, username, password }) {
  const [status, setStatus] = useState(null);
  async function test(){
    setStatus('testing');
    try{const d=await apiFetch(`${API}/client-reg/test-connection`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({server,port,database,username,password})}).then(r=>r.json());setStatus(d);}
    catch(e){setStatus({ok:false,error:e.message});}
  }
  return (
    <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
      <button onClick={test} disabled={status==='testing'} className="erp-btn" data-testid="test-connection-btn" style={{display:'flex',alignItems:'center',gap:4}}>
        {status==='testing'?<><Loader size={11}/> Testing…</>:<><Wifi size={11}/> Test Connection</>}
      </button>
      {status&&status!=='testing'&&(
        status.ok
          ?<span style={{fontSize:11,color:'green',display:'flex',alignItems:'center',gap:4}}><CheckCircle size={11}/> {status.dbName}</span>
          :<span style={{fontSize:11,color:'#dc2626',display:'flex',alignItems:'center',gap:4}}><WifiOff size={11}/> {String(status.error||'').slice(0,80)}</span>
      )}
    </div>
  );
}

// ── SQL Ping Dot (per client row) ─────────────────────────────────────────────
function SqlPingDot({ row, pingCache, setPingCache }) {
  const key = row.ClientId;
  const st  = pingCache[key];
  async function ping(){
    if(!row.OnPremSQLServer) return;
    setPingCache(p=>({...p,[key]:'pinging'}));
    try{
      const d=await apiFetch(`${API}/client-reg/test-connection`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({server:row.OnPremSQLServer,port:row.OnPremSQLPort||1433,database:row.OnPremSQLDB,username:row.OnPremSQLUser})}).then(r=>r.json());
      setPingCache(p=>({...p,[key]:d.ok?'ok':'fail'}));
    }catch{setPingCache(p=>({...p,[key]:'fail'}));}
  }
  if(!row.OnPremSQLServer) return <span style={{fontSize:10,color:'#94a3b8'}}>N/A</span>;
  const colors={ok:'#16a34a',fail:'#dc2626',pinging:'#f59e0b',default:'#d1d5db'};
  const color=colors[st]||colors.default;
  return (
    <button onClick={e=>{e.stopPropagation();ping();}} title={`Ping ${row.OnPremSQLServer}`} style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center',gap:4}}>
      {st==='pinging'?<Loader size={10} style={{color:'#f59e0b'}}/>:<span style={{width:10,height:10,borderRadius:'50%',background:color,display:'inline-block'}}/>}
      <span style={{fontSize:10,color}}>{st==='ok'?'Online':st==='fail'?'Offline':st==='pinging'?'…':'Ping'}</span>
    </button>
  );
}

// ── 4-Step Registration Wizard ────────────────────────────────────────────────
function RegistrationWizard({ initial={}, modules=[], isSuperAdmin, onClose, onDone, countries=[] }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [showPass, setSP] = useState(false);
  const [errors, setErrors] = useState({});
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstResult, setGstResult]       = useState(null);
  const gstDebounceRef                  = useRef(null);
  const [otpSent, setOtpSent]           = useState(false);
  const [otpValue, setOtpValue]         = useState('');
  const [otpVerified, setOtpVerified]   = useState(false);
  const [otpBusy, setOtpBusy]           = useState(false);
  const [otpMsg, setOtpMsg]             = useState(null);   // {ok,text}
  const [emailErr, setEmailErr]         = useState('');

  const [form, setForm] = useState({
    companyName:  initial.companyName||'',
    contactName:  initial.contactName||'',
    email:        initial.email||'',
    phone:        initial.phone||'',
    gst:          (initial.gst||'').toUpperCase(),
    pan:          (initial.pan||'').toUpperCase(),
    address:      initial.address||'',
    city:         initial.city||'',
    state:        initial.state||'',
    pincode:      initial.pincode||'',
    website:      initial.website||'',
    country:      initial.country||'IND',
    clientType:   'ONPREMISES',
    onPremServer: '', onPremPort: '1433', onPremDB: '', onPremSQLUser: '', onPremSQLPass: '',
    cloudServer:  '', cloudDB: '',
    serviceType:  'ERP',
    appPrivileges: modules.map(m=>({...m,enabled:true})),
    leadSourceId:  initial.leadId||'',
    existingExpiryDate: initial.expiryDate||null,
    serviceURLsRaw: '{"BI":"https://veriteqcliq.in/mobileBI/"}',
    industryCode:  initial.industryCode||'',
  });

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const needsOnPrem = ['ONPREMISES','HYBRID','CLOUD_MIGRATION'].includes(form.clientType);
  const needsCloud  = ['HYBRID','CLOUD_MIGRATION'].includes(form.clientType);

  function validateStep1(){
    const e={};
    if(!form.companyName.trim())e.companyName='Company name is required';
    if(!form.email.trim())e.email='Email is required';
    else if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))e.email='Invalid email';
    if(!form.phone.trim())e.phone='Phone number is required';
    else if(!/^\d{7,15}$/.test(form.phone.replace(/[\s+\-()]/g,'')))e.phone='Enter a valid phone number';
    if(form.gst&&!validateGST(form.gst))e.gst='Invalid GSTIN (e.g. 22AAAAA0000A1Z5)';
    setErrors(e);
    return Object.keys(e).length===0;
  }

  function goNext(){ if(step===1&&!validateStep1())return; setStep(s=>s+1); }

  async function sendOtp() {
    const phone = form.phone.replace(/\D/g,'');
    if (phone.length < 7) { setOtpMsg({ok:false,text:'Enter a valid phone number first'}); return; }
    setOtpBusy(true); setOtpMsg(null);
    try {
      const d = await apiFetch(`${API}/client-reg/send-whatsapp-otp`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({phone})}).then(r=>r.json());
      setOtpSent(true); setOtpMsg({ok:d.ok, text: d.message||d.error||''});
    } catch(e){ setOtpMsg({ok:false,text:e.message}); }
    finally{ setOtpBusy(false); }
  }

  async function verifyOtp() {
    setOtpBusy(true); setOtpMsg(null);
    try {
      const phone = form.phone.replace(/\D/g,'');
      const d = await apiFetch(`${API}/client-reg/verify-whatsapp-otp`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({phone,otp:otpValue})}).then(r=>r.json());
      if(d.ok){ setOtpVerified(true); setOtpMsg({ok:true,text:'Mobile number verified!'}); }
      else { setOtpMsg({ok:false,text:d.error||'Incorrect OTP'}); }
    } catch(e){ setOtpMsg({ok:false,text:e.message}); }
    finally{ setOtpBusy(false); }
  }

  async function verifyGST(gst) {
    if (!gst || !validateGST(gst)) return;
    setGstVerifying(true); setGstResult(null);
    try {
      const d = await apiFetch(`${API}/client-reg/verify-gstin/${gst.trim().toUpperCase()}`).then(r => r.json());
      setGstResult(d);
      if (d.success && d.data) {
        const name = d.data.lgnm || d.data.tradeName || d.data.tradeNam || '';
        const addr = d.data.pradr?.addr || {};
        if (name && !form.companyName) set('companyName', name);
        if (addr.st && !form.state) set('state', addr.st);
        if (addr.pncd && !form.pincode) set('pincode', String(addr.pncd));
      }
    } catch(e) { setGstResult({ success: false, message: e.message }); }
    finally { setGstVerifying(false); }
  }

  // Auto-verify on debounce when full 15-char valid GSTIN is typed
  useEffect(() => {
    clearTimeout(gstDebounceRef.current);
    const gst = form.gst;
    // Instant state map from first 2 digits (no API needed)
    if (gst && gst.length >= 2) {
      const stateFromCode = GST_STATE_MAP[gst.substring(0, 2)];
      if (stateFromCode && !form.state) set('state', stateFromCode);
    }
    if (gst && validateGST(gst)) {
      gstDebounceRef.current = setTimeout(() => verifyGST(gst), 800);
    } else {
      setGstResult(null);
    }
    return () => clearTimeout(gstDebounceRef.current);
  }, [form.gst]); // eslint-disable-line

  async function submit(){
    setSaving(true);
    try{
      const d=await apiFetch(`${API}/client-reg/register`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(form)}).then(r=>r.json());
      if(d.ok){setResult(d);onDone();}else throw new Error(d.error||'Registration failed');
    }catch(e){alert(e.message);}
    finally{setSaving(false);}
  }

  const STEPS=['Company Info','Deployment Type','Connection Details','Services'];

  return (
    <div style={{position:'fixed',inset:0,zIndex:1100,display:'flex',alignItems:'flex-start',justifyContent:'flex-end',background:'rgba(0,0,0,0.4)'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:540,height:'100%',background:'var(--erp-surface)',borderLeft:'1px solid var(--erp-border)',display:'flex',flexDirection:'column',overflow:'hidden',boxShadow:'var(--erp-shadow-2)'}}>
        {/* Header */}
        <div style={{padding:'12px 16px',borderBottom:'1px solid var(--erp-border)',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fafbfc'}}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:'var(--erp-text)'}}>Register Client</div>
            <div style={{fontSize:11,color:'var(--erp-text-muted)',marginTop:2}}>Step {step}/{STEPS.length} — {STEPS[step-1]}</div>
          </div>
          <button onClick={onClose} data-testid="wizard-close" style={{background:'none',border:'none',cursor:'pointer',color:'var(--erp-text-muted)'}}><X size={16}/></button>
        </div>
        {/* Progress bar */}
        <div style={{display:'flex',borderBottom:'1px solid var(--erp-border)',background:'#f8fafc'}}>
          {STEPS.map((s,i)=>(
            <div key={i} style={{flex:1,padding:'6px 3px',borderRight:i<STEPS.length-1?'1px solid var(--erp-border)':'none',textAlign:'center',background:i+1===step?'#eff6ff':'transparent'}}>
              <div style={{fontSize:10,fontWeight:700,color:i+1<=step?'var(--erp-primary)':'var(--erp-text-muted)'}}>{i+1}. {s}</div>
            </div>
          ))}
        </div>
        {/* Body */}
        <div style={{flex:1,overflowY:'auto',padding:'14px 16px'}}>
          {result ? (
            <div style={{textAlign:'center',padding:'30px 0'}}>
              <CheckCircle size={40} color="var(--erp-success)" style={{marginBottom:12}}/>
              <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>Client Registered!</div>
              <div style={{fontSize:12,color:'var(--erp-text-muted)'}}>ID: <code style={{color:'var(--erp-primary)'}}>{result.clientId}</code></div>
              {result.credentials&&(
                <div style={{background:'#f8fafc',border:'1px solid var(--erp-border)',borderRadius:6,padding:12,textAlign:'left',marginTop:12,fontSize:12}}>
                  <div style={{fontSize:10,fontWeight:700,marginBottom:6}}>LOGIN CREDENTIALS</div>
                  <div>Admin: <code>{result.credentials.admin}</code> / <code>{result.credentials.adminPass}</code></div>
                  <div>SysCLIQ: <code>{result.credentials.licensor}</code> / aaaaaa99</div>
                  <div>ssadmin: <code>{result.credentials.ssadmin}</code> / aaaaaa12</div>
                </div>
              )}
              {result.licenseId&&<div style={{fontSize:11,color:'green',marginTop:8}}>License: <code>{result.licenseId}</code></div>}
              <button onClick={onClose} className="erp-btn erp-btn-primary" style={{marginTop:16}}>Close</button>
            </div>
          ) : (
            <>
              {/* STEP 1 — Company Info */}
              {step===1&&(
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {form.existingExpiryDate&&(
                    <div style={{background:'#f0fdf4',border:'1px solid var(--erp-success)',borderRadius:4,padding:'8px 10px',fontSize:11,display:'flex',alignItems:'center',gap:6}}>
                      <CheckCircle size={12} color="var(--erp-success)"/><strong>Existing Customer</strong> — Expiry: {fmtDate(form.existingExpiryDate)}
                    </div>
                  )}
                  <div><label style={lbl}>Company Name <span style={{color:'#dc2626'}}>*</span></label>
                    <input data-testid="wiz-companyName" value={form.companyName} onChange={e=>{set('companyName',e.target.value);setErrors(v=>({...v,companyName:''}));}} style={errors.companyName?inpErr:inp}/>
                    {errors.companyName&&<div style={{fontSize:10,color:'#dc2626',marginTop:2}}>{errors.companyName}</div>}
                  </div>
                  <div><label style={lbl}>Email <span style={{color:'#dc2626'}}>*</span></label>
                    <input data-testid="wiz-email" type="email" value={form.email}
                      onChange={e=>{set('email',e.target.value);setErrors(v=>({...v,email:''}));setEmailErr('');}}
                      onBlur={e=>{const v=e.target.value.trim();if(v&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))setEmailErr('Invalid email address');}}
                      style={errors.email||emailErr?inpErr:inp}/>
                    {(errors.email||emailErr)&&<div style={{fontSize:10,color:'#dc2626',marginTop:2}}>{errors.email||emailErr}</div>}
                    {form.email&&!errors.email&&!emailErr&&/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)&&<div style={{fontSize:10,color:'#16a34a',marginTop:2}}>✓ Valid email</div>}
                  </div>
                  <div><label style={lbl}>Phone <span style={{color:'#dc2626'}}>*</span></label>
                    <div style={{display:'flex',gap:6,alignItems:'flex-start',flexWrap:'wrap'}}>
                      <div style={{flex:1}}>
                        <input data-testid="wiz-phone" value={form.phone}
                          onChange={e=>{set('phone',e.target.value);setErrors(v=>({...v,phone:''}));setOtpSent(false);setOtpVerified(false);setOtpMsg(null);setOtpValue('');}}
                          style={{...(errors.phone?inpErr:inp),borderColor:otpVerified?'#16a34a':undefined}}/>
                        {errors.phone&&<div style={{fontSize:10,color:'#dc2626',marginTop:2}}>{errors.phone}</div>}
                      </div>
                      {!otpVerified&&(
                        <button type="button" onClick={sendOtp} disabled={otpBusy||!form.phone.trim()} className="erp-btn" data-testid="wiz-send-otp"
                          style={{display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap',marginTop:0,fontSize:11}}>
                          {otpBusy&&!otpSent?<><Loader size={10}/> Sending…</>:<><Wifi size={10}/> {otpSent?'Resend OTP':'Verify via WA'}</>}
                        </button>
                      )}
                      {otpVerified&&<span style={{fontSize:11,color:'#16a34a',display:'flex',alignItems:'center',gap:4,padding:'6px 0'}}><CheckCircle size={12}/> Verified</span>}
                    </div>
                    {otpSent&&!otpVerified&&(
                      <div style={{display:'flex',gap:6,marginTop:6,alignItems:'center'}}>
                        <input data-testid="wiz-otp-input" value={otpValue} onChange={e=>setOtpValue(e.target.value.replace(/\D/g,'').slice(0,6))}
                          placeholder="6-digit OTP" maxLength={6} style={{...inp,width:120,letterSpacing:4,textAlign:'center'}}/>
                        <button type="button" onClick={verifyOtp} disabled={otpBusy||otpValue.length!==6} className="erp-btn erp-btn-primary" data-testid="wiz-verify-otp" style={{fontSize:11}}>
                          {otpBusy?<Loader size={10}/>:'Confirm OTP'}
                        </button>
                      </div>
                    )}
                    {otpMsg&&<div style={{fontSize:10,color:otpMsg.ok?'#16a34a':'#dc2626',marginTop:2}}>{otpMsg.text}</div>}
                  </div>
                  <div><label style={lbl}>Contact Person</label>
                    <input data-testid="wiz-contactName" value={form.contactName} onChange={e=>set('contactName',e.target.value)} style={inp}/>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    <div><label style={lbl}>Industry Code</label>
                      <input data-testid="wiz-industryCode" value={form.industryCode} onChange={e=>set('industryCode',e.target.value.toUpperCase())} style={inp} placeholder="e.g. GARMENTS"/>
                    </div>
                    <div><label style={lbl}>Country</label>
                      {countries.length > 0 ? (
                        <select data-testid="wiz-country" value={form.country} onChange={e=>set('country',e.target.value)} style={inp}>
                          <option value="">Select country…</option>
                          {countries.map(c=><option key={c.CountryCode} value={c.CountryCode}>{c.CountryName} ({c.CountryCode})</option>)}
                        </select>
                      ) : (
                        <input data-testid="wiz-country" value={form.country} onChange={e=>set('country',e.target.value.toUpperCase())} style={inp} placeholder="IND"/>
                      )}
                    </div>
                  </div>
                  <div><label style={lbl}>GST Number</label>
                    <div style={{position:'relative'}}>
                      <input data-testid="wiz-gst" value={form.gst} onChange={e=>{set('gst',e.target.value.toUpperCase());setErrors(v=>({...v,gst:''}));}} style={{...( errors.gst?inpErr:inp),paddingRight:28}} placeholder="e.g. 22AAAAA0000A1Z5"/>
                      {gstVerifying&&<Loader size={12} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',color:'#f59e0b'}}/>}
                      {!gstVerifying&&gstResult&&(gstResult.success
                        ?<CheckCircle size={12} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',color:'#16a34a'}}/>
                        :<XCircle size={12} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',color:'#f59e0b'}}/>
                      )}
                    </div>
                    {errors.gst&&<div style={{fontSize:10,color:'#dc2626',marginTop:2}}>{errors.gst}</div>}
                    {!errors.gst&&gstResult&&(gstResult.success
                      ?<div style={{fontSize:10,color:'#16a34a',marginTop:2}}>✓ {gstResult.data?.lgnm||gstResult.data?.tradeName||'Valid — details auto-filled'}</div>
                      :<div style={{fontSize:10,color:'#f59e0b',marginTop:2}}>{gstResult.message||'Not found in GST registry'}</div>
                    )}
                    {!errors.gst&&!gstResult&&form.gst&&form.gst.length===15&&!validateGST(form.gst)&&<div style={{fontSize:10,color:'#dc2626',marginTop:2}}>Invalid GSTIN format</div>}
                  </div>
                  <div><label style={lbl}>PAN Number</label>
                    <input data-testid="wiz-pan" value={form.pan} onChange={e=>set('pan',e.target.value.toUpperCase())} style={inp}/>
                  </div>
                  <div><label style={lbl}>Address</label>
                    <input data-testid="wiz-address" value={form.address} onChange={e=>set('address',e.target.value)} style={inp}/>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    <div><label style={lbl}>City</label><input data-testid="wiz-city" value={form.city} onChange={e=>set('city',e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>State</label><input data-testid="wiz-state" value={form.state} onChange={e=>set('state',e.target.value)} style={inp}/></div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    <div><label style={lbl}>Pincode</label><input data-testid="wiz-pincode" value={form.pincode} onChange={e=>set('pincode',e.target.value)} style={inp}/></div>
                    <div><label style={lbl}>Website</label><input data-testid="wiz-website" value={form.website} onChange={e=>set('website',e.target.value)} style={inp}/></div>
                  </div>
                </div>
              )}

              {/* STEP 2 — Deployment Type */}
              {step===2&&(
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontSize:11,color:'var(--erp-text-muted)',marginBottom:4}}>Select the deployment type for this client:</div>
                  {CLIENT_TYPES.map(ct=>(
                    <div key={ct.id}>
                      <button data-testid={`wiz-type-${ct.id}`} onClick={()=>set('clientType',ct.id)}
                        style={{width:'100%',padding:'10px 12px',borderRadius:4,textAlign:'left',cursor:'pointer',border:`2px solid ${form.clientType===ct.id?'var(--erp-primary)':'var(--erp-border)'}`,background:form.clientType===ct.id?'#eff6ff':'#fafbfc',color:form.clientType===ct.id?'var(--erp-primary)':'var(--erp-text)',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:8}}>
                        <span style={{width:13,height:13,borderRadius:'50%',border:`2px solid ${form.clientType===ct.id?'var(--erp-primary)':'#cbd5e1'}`,background:form.clientType===ct.id?'var(--erp-primary)':'transparent',flexShrink:0}}/>
                        {ct.label}
                      </button>
                      {form.clientType===ct.id&&(
                        <div style={{padding:'10px 12px',border:'1px solid #bfdbfe',borderTop:'none',borderRadius:'0 0 4px 4px',background:'#f0f7ff',fontSize:11,color:'#1e40af'}}>
                          {ct.desc}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* STEP 3 — Connection Details */}
              {step===3&&(
                <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {!needsOnPrem?(
                    <div style={{fontSize:12,color:'var(--erp-success)',display:'flex',alignItems:'center',gap:6,padding:'12px 0'}}>
                      <CheckCircle size={14}/> SaaS deployment — no SQL Server configuration required.
                    </div>
                  ):(
                    <>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--erp-warning)',display:'flex',alignItems:'center',gap:4,marginBottom:4}}><Server size={11}/> On-Premises SQL Server</div>
                      <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:8}}>
                        <div><label style={lbl}>SQL Server IP / Hostname</label>
                          <input data-testid="wiz-onPremServer" value={form.onPremServer} onChange={e=>set('onPremServer',e.target.value)} style={inp} placeholder="e.g. 192.168.1.100"/>
                        </div>
                        <div><label style={lbl}>Port</label>
                          <input data-testid="wiz-onPremPort" value={form.onPremPort} onChange={e=>set('onPremPort',e.target.value)} style={inp}/>
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <div><label style={lbl}>Database Name</label>
                          <input data-testid="wiz-onPremDB" value={form.onPremDB} onChange={e=>set('onPremDB',e.target.value)} style={inp}/>
                        </div>
                        <div><label style={lbl}>SQL Username</label>
                          <input data-testid="wiz-onPremSQLUser" value={form.onPremSQLUser} onChange={e=>set('onPremSQLUser',e.target.value)} style={inp}/>
                        </div>
                      </div>
                      <div><label style={lbl}>SQL Password</label>
                        <div style={{position:'relative'}}>
                          <input data-testid="wiz-onPremSQLPass" type={showPass?'text':'password'} value={form.onPremSQLPass} onChange={e=>set('onPremSQLPass',e.target.value)} style={{...inp,paddingRight:32}}/>
                          <button type="button" onClick={()=>setSP(s=>!s)} style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}>{showPass?<EyeOff size={12}/>:<Eye size={12}/>}</button>
                        </div>
                      </div>
                      <ConnectionTester server={form.onPremServer} port={form.onPremPort} database={form.onPremDB} username={form.onPremSQLUser} password={form.onPremSQLPass}/>
                      {needsCloud&&(
                        <div style={{marginTop:8,paddingTop:10,borderTop:'1px dashed var(--erp-border)'}}>
                          <div style={{fontSize:11,fontWeight:700,color:'var(--erp-primary)',display:'flex',alignItems:'center',gap:4,marginBottom:8}}><Database size={11}/> Cloud Target Server</div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                            <div><label style={lbl}>Cloud SQL Server</label><input data-testid="wiz-cloudServer" value={form.cloudServer} onChange={e=>set('cloudServer',e.target.value)} style={inp}/></div>
                            <div><label style={lbl}>Cloud Database</label><input data-testid="wiz-cloudDB" value={form.cloudDB} onChange={e=>set('cloudDB',e.target.value)} style={inp}/></div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* STEP 4 — Services + Privileges */}
              {step===4&&(
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  <div><label style={lbl}>Service Type</label>
                    <div style={{display:'flex',gap:6}}>
                      {SERVICE_TYPES.map(st=>(
                        <button key={st.id} data-testid={`wiz-svc-${st.id}`} onClick={()=>set('serviceType',st.id)}
                          style={{padding:'6px 14px',border:`2px solid ${form.serviceType===st.id?'var(--erp-primary)':'var(--erp-border)'}`,borderRadius:4,background:form.serviceType===st.id?'#eff6ff':'#fff',cursor:'pointer',fontSize:11,fontWeight:700,color:form.serviceType===st.id?'var(--erp-primary)':'var(--erp-text-muted)'}}>
                          {st.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {form.serviceType?.includes('BI')&&(
                    <div><label style={lbl}>Service URLs (JSON) — BI + App</label>
                      <textarea data-testid="wiz-serviceURLs" rows={4} value={form.serviceURLsRaw} onChange={e=>set('serviceURLsRaw',e.target.value)} style={{...inp,fontFamily:'monospace',resize:'vertical'}} placeholder='{"BI":"https://..."}' />
                    </div>
                  )}
                  {!form.serviceType?.includes('BI')&&form.clientType!=='SAAS'&&(
                    <div><label style={lbl}>Service URLs (JSON) <span style={{fontSize:10,color:'var(--erp-text-muted)'}}>— ERP / Remote access / App links</span></label>
                      <textarea data-testid="wiz-serviceURLs" rows={3} value={form.serviceURLsRaw} onChange={e=>set('serviceURLsRaw',e.target.value)} style={{...inp,fontFamily:'monospace',resize:'vertical'}} placeholder='{"ERP":"https://...","RemoteApp":"https://..."}' />
                    </div>
                  )}
                  {isSuperAdmin&&modules.length>0&&(
                    <div>
                      <label style={{...lbl,marginBottom:8}}>Module Privileges</label>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5}}>
                        {form.appPrivileges.map((m,idx)=>(
                          <label key={m.id} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 8px',border:'1px solid var(--erp-border)',borderRadius:4,cursor:'pointer',fontSize:11}}>
                            <input type="checkbox" checked={m.enabled} onChange={e=>{const u=[...form.appPrivileges];u[idx]={...m,enabled:e.target.checked};set('appPrivileges',u);}} style={{accentColor:'var(--erp-primary)'}}/>
                            {m.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Summary */}
                  <div style={{background:'#f8fafc',border:'1px solid var(--erp-border)',borderRadius:6,padding:12,fontSize:12}}>
                    <div style={{fontSize:11,fontWeight:700,marginBottom:6}}>Registration Summary</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,fontSize:11,color:'var(--erp-text-muted)'}}>
                      <div>Company: <strong style={{color:'var(--erp-text)'}}>{form.companyName}</strong></div>
                      <div>Email: <strong style={{color:'var(--erp-text)'}}>{form.email}</strong></div>
                      <div>Phone: <strong style={{color:'var(--erp-text)'}}>{form.phone}</strong></div>
                      <div>Type: <strong style={{color:'var(--erp-text)'}}>{form.clientType}</strong></div>
                      {form.onPremServer&&<div>SQL: <strong style={{color:'var(--erp-text)'}}>{form.onPremServer}</strong></div>}
                      <div>Service: <strong style={{color:'var(--erp-text)'}}>{form.serviceType}</strong></div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        {/* Footer */}
        {!result&&(
          <div style={{padding:'10px 16px',borderTop:'1px solid var(--erp-border)',display:'flex',justifyContent:'space-between',background:'#fafbfc'}}>
            <button onClick={()=>step>1?setStep(s=>s-1):onClose()} data-testid="wiz-back" className="erp-btn" style={{display:'flex',alignItems:'center',gap:4}}>
              <ChevronLeft size={12}/>{step>1?'Back':'Cancel'}
            </button>
            {step<STEPS.length
              ?<button onClick={goNext} data-testid="wiz-next" className="erp-btn erp-btn-primary" style={{display:'flex',alignItems:'center',gap:4}}>Next <ChevronRight size={12}/></button>
              :<button onClick={submit} disabled={saving} data-testid="wiz-submit" className="erp-btn erp-btn-primary">{saving?'Registering…':'Register Client'}</button>
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ── Connection Edit Modal ─────────────────────────────────────────────────────
function ConnectionEditModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    onPremServer:client.OnPremSQLServer||'',onPremPort:String(client.OnPremSQLPort||1433),
    onPremDB:client.OnPremSQLDB||'',onPremSQLUser:client.OnPremSQLUser||'',onPremSQLPass:'',
    cloudServer:client.CloudSQLServer||'',cloudDB:client.CloudSQLDB||'',
    serviceURLs:(()=>{try{return JSON.stringify(JSON.parse(client.ServiceURLs||'{}'),null,2);}catch{return '{}';}})(),
  });
  const [showPass,setSP]=useState(false);
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  async function save(){
    setSaving(true);
    try{
      let svcURLs=form.serviceURLs;try{svcURLs=JSON.parse(form.serviceURLs);}catch(_){}
      const payload={...form,serviceURLs:svcURLs};if(!payload.onPremSQLPass)delete payload.onPremSQLPass;
      await apiFetch(`${API}/client-reg/client/${client.ClientId}/connection`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      onSaved();
    }catch(e){alert(e.message);}
    finally{setSaving(false);}
  }
  return (
    <div style={{position:'fixed',inset:0,zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:500,maxHeight:'80vh',background:'var(--erp-surface)',borderRadius:'var(--erp-radius-lg)',border:'1px solid var(--erp-border)',boxShadow:'var(--erp-shadow-2)',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'10px 14px',borderBottom:'1px solid var(--erp-border)',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#fafbfc'}}>
          <div style={{fontSize:13,fontWeight:700}}>Edit Connection — {client.CompanyName||client.ClientId}</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--erp-text-muted)'}}><X size={14}/></button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:14,display:'flex',flexDirection:'column',gap:10}}>
          <div style={{fontSize:11,fontWeight:700,color:'var(--erp-warning)',display:'flex',alignItems:'center',gap:5}}><Server size={11}/> On-Premises SQL</div>
          {[['onPremServer','SQL Server / IP'],['onPremPort','Port'],['onPremDB','Database'],['onPremSQLUser','SQL Username']].map(([k,l])=>(
            <div key={k}><label style={lbl}>{l}</label><input data-testid={`conn-${k}`} value={form[k]} onChange={e=>set(k,e.target.value)} style={inp}/></div>
          ))}
          <div><label style={lbl}>SQL Password (blank = keep)</label>
            <div style={{position:'relative'}}>
              <input data-testid="conn-sqlPass" type={showPass?'text':'password'} value={form.onPremSQLPass} onChange={e=>set('onPremSQLPass',e.target.value)} style={{...inp,paddingRight:32}} placeholder="Enter new or leave blank"/>
              <button type="button" onClick={()=>setSP(s=>!s)} style={{position:'absolute',right:7,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}>{showPass?<EyeOff size={12}/>:<Eye size={12}/>}</button>
            </div>
          </div>
          <div style={{fontSize:11,fontWeight:700,color:'var(--erp-primary)',display:'flex',alignItems:'center',gap:5}}><Database size={11}/> Cloud / Secondary</div>
          {[['cloudServer','Cloud Server'],['cloudDB','Cloud DB']].map(([k,l])=>(
            <div key={k}><label style={lbl}>{l}</label><input data-testid={`conn-${k}`} value={form[k]} onChange={e=>set(k,e.target.value)} style={inp}/></div>
          ))}
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'#7c3aed',marginBottom:5,display:'flex',alignItems:'center',gap:5}}><Link2 size={11}/> Service URLs (JSON)</div>
            <textarea data-testid="conn-serviceURLs" rows={4} value={form.serviceURLs} onChange={e=>set('serviceURLs',e.target.value)} style={{...inp,fontFamily:'monospace',resize:'vertical'}}/>
          </div>
          <ConnectionTester server={form.onPremServer} port={form.onPremPort} database={form.onPremDB} username={form.onPremSQLUser} password={form.onPremSQLPass}/>
        </div>
        <div style={{padding:'10px 14px',borderTop:'1px solid var(--erp-border)',display:'flex',justifyContent:'flex-end',gap:8,background:'#fafbfc'}}>
          <button onClick={onClose} className="erp-btn">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="conn-save-btn" className="erp-btn erp-btn-primary">{saving?'Saving…':'Save Connection'}</button>
        </div>
      </div>
    </div>
  );
}

// ── SQL Connectivity Tab ──────────────────────────────────────────────────────
function SqlConnectivityTab() {
  const [allClients, setAllClients] = useState([]);
  const [pingMap, setPingMap]       = useState({});   // clientId → 'idle'|'pinging'|{ok,dbName,error}
  const [loaded, setLoaded]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [pingingAll, setPingingAll] = useState(false);
  const abortRef = useRef(false);

  async function loadClients() {
    setLoading(true); setLoaded(false);
    try {
      const d = await apiFetch(`${API}/client-reg/clients?pageSize=1000`).then(r => r.json());
      const hasSql = (d.clients||[]).filter(c => c.OnPremSQLServer);
      setAllClients(hasSql); setLoaded(true);
    } catch { setAllClients([]); }
    finally { setLoading(false); }
  }

  async function pingOne(c) {
    setPingMap(p => ({ ...p, [c.ClientId]: 'pinging' }));
    try {
      const d = await apiFetch(`${API}/client-reg/test-connection`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ server: c.OnPremSQLServer, port: c.OnPremSQLPort||1433, database: c.OnPremSQLDB, username: c.OnPremSQLUser }),
      }).then(r => r.json());
      setPingMap(p => ({ ...p, [c.ClientId]: d }));
    } catch(e) { setPingMap(p => ({ ...p, [c.ClientId]: { ok: false, error: e.message } })); }
  }

  async function pingAll() {
    if (!loaded) await loadClients();
    setPingingAll(true); abortRef.current = false;
    // concurrency of 5
    const queue = [...allClients];
    const worker = async () => {
      while (queue.length && !abortRef.current) {
        const c = queue.shift();
        await pingOne(c);
      }
    };
    await Promise.all([worker(), worker(), worker(), worker(), worker()]);
    setPingingAll(false);
  }

  function stopPing() { abortRef.current = true; setPingingAll(false); }

  const stats = allClients.reduce((acc, c) => {
    const s = pingMap[c.ClientId];
    if (!s || s === 'pinging') acc.unknown++;
    else if (s === 'idle') acc.unknown++;
    else if (s.ok) acc.online++;
    else acc.offline++;
    return acc;
  }, { online: 0, offline: 0, unknown: 0 });

  return (
    <div style={{ padding: '12px 16px', height: '100%', overflowY: 'auto' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--erp-text)', flex: 1 }}>
          SQL Server Connectivity Check — On-Demand Only
        </div>
        {loaded && (
          <div style={{ display: 'flex', gap: 8, fontSize: 11 }}>
            <span style={{ color: '#16a34a', fontWeight: 700 }}>● {stats.online} Online</span>
            <span style={{ color: '#dc2626', fontWeight: 700 }}>● {stats.offline} Offline</span>
            <span style={{ color: '#94a3b8', fontWeight: 700 }}>● {stats.unknown} Unknown</span>
          </div>
        )}
        {!loaded && !loading && (
          <button className="erp-btn" onClick={loadClients} data-testid="sql-load-btn">Load Clients</button>
        )}
        {loaded && !pingingAll && (
          <button className="erp-btn erp-btn-primary" onClick={pingAll} data-testid="sql-ping-all-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Wifi size={12} /> Ping All ({allClients.length})
          </button>
        )}
        {pingingAll && (
          <button className="erp-btn" onClick={stopPing} data-testid="sql-stop-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <X size={12} /> Stop
          </button>
        )}
        {loading && <span style={{ fontSize: 11, color: 'var(--erp-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}><Loader size={11} /> Loading…</span>}
      </div>

      {!loaded && !loading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--erp-text-muted)', fontSize: 12 }}>
          Click <strong>Load Clients</strong> to fetch on-prem clients, then <strong>Ping All</strong> to check connectivity.
          <br/><span style={{ fontSize: 11, marginTop: 4, display: 'block' }}>No network traffic until you explicitly start a ping.</span>
        </div>
      )}

      {loaded && allClients.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--erp-text-muted)', fontSize: 12 }}>No on-premises clients found with SQL Server configured.</div>
      )}

      {loaded && allClients.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--erp-header-bg)', borderBottom: '2px solid var(--erp-border)' }}>
              {['Status','Client','Server','Port','Database','User','Action'].map(h => (
                <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--erp-text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allClients.map((c, i) => {
              const s = pingMap[c.ClientId];
              const isPinging = s === 'pinging';
              const isOk  = s && s !== 'pinging' && s.ok;
              const isFail= s && s !== 'pinging' && !s.ok && s.ok !== undefined;
              const dotColor = isPinging ? '#f59e0b' : isOk ? '#16a34a' : isFail ? '#dc2626' : '#d1d5db';
              return (
                <tr key={c.ClientId} style={{ borderBottom: '1px solid var(--erp-border)', background: i % 2 === 0 ? '#fafbfc' : '#fff' }}>
                  <td style={{ padding: '6px 8px' }}>
                    {isPinging
                      ? <Loader size={11} style={{ color: '#f59e0b' }} />
                      : <span style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, display: 'inline-block' }} />
                    }
                    <span style={{ fontSize: 10, marginLeft: 4, color: dotColor }}>
                      {isPinging ? 'Pinging…' : isOk ? 'Online' : isFail ? 'Offline' : 'Unknown'}
                    </span>
                  </td>
                  <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--erp-text)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.CompanyName}>{c.CompanyName||c.ClientId}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>{c.OnPremSQLServer}</td>
                  <td style={{ padding: '6px 8px', fontSize: 11 }}>{c.OnPremSQLPort||1433}</td>
                  <td style={{ padding: '6px 8px', fontSize: 11 }}>{c.OnPremSQLDB}</td>
                  <td style={{ padding: '6px 8px', fontSize: 11 }}>{c.OnPremSQLUser}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <button onClick={() => pingOne(c)} disabled={isPinging} className="erp-btn" style={{ fontSize: 10, padding: '2px 8px' }} data-testid={`ping-btn-${c.ClientId}`}>
                      {isPinging ? '…' : 'Ping'}
                    </button>
                    {isFail && <span style={{ fontSize: 10, color: '#dc2626', marginLeft: 6 }} title={s.error}>{String(s.error||'').slice(0,30)}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
// ── MobileBI Login Modal ──────────────────────────────────────────────────────
function MBILoginModal({ client, onClose, onResetPwd }) {
  const MBI_URL = 'https://veriteqcliq.in/mobileBI/';
  const [copied, setCopied] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);
  function copy(text, label) {
    navigator.clipboard.writeText(text).then(()=>{setCopied(label);setTimeout(()=>setCopied(''),1800);}).catch(()=>{});
  }
  async function resetPwd() {
    setResetting(true); setResetMsg(null);
    try {
      const d = await apiFetch(`${API}/client-reg/mobilbi/user/${client.id}/reset-password`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({password:'aaaaaa12'})}).then(r=>r.json());
      if(d.ok) { setResetMsg({ok:true,text:`Password reset to: ${d.newPassword}`}); onResetPwd(); }
      else setResetMsg({ok:false,text:d.error||'Failed'});
    } catch(e){ setResetMsg({ok:false,text:e.message}); }
    finally{ setResetting(false); }
  }
  return (
    <div style={{position:'fixed',inset:0,zIndex:2200,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.45)'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:400,background:'#fff',borderRadius:8,border:'1px solid #e2e8f0',boxShadow:'0 8px 32px rgba(0,0,0,0.18)',overflow:'hidden'}}>
        <div style={{padding:'12px 16px',background:'#1e3a5f',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <BarChart2 size={16} color="#60a5fa"/>
            <span style={{fontSize:13,fontWeight:700,color:'#fff'}}>MobileBI Login — {client.company||client.name}</span>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}><X size={14}/></button>
        </div>
        <div style={{padding:16,display:'flex',flexDirection:'column',gap:12}}>
          <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:4,padding:'8px 12px',fontSize:12,color:'#15803d'}}>
            Use these credentials to log in to MobileBI reports.
          </div>
          {[['Email', client.email],['Company', client.company||client.name]].map(([lbl,val])=>(
            <div key={lbl} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:'#f8fafc',borderRadius:4,border:'1px solid #e2e8f0'}}>
              <div><div style={{fontSize:10,color:'#64748b',fontWeight:600}}>{lbl}</div><div style={{fontSize:12,fontWeight:700,color:'#1e293b',marginTop:2}}>{val}</div></div>
              <button onClick={()=>copy(val,lbl)} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b',display:'flex',alignItems:'center',gap:3,fontSize:11}}>
                <Copy size={11}/>{copied===lbl?'Copied!':'Copy'}
              </button>
            </div>
          ))}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 10px',background:'#fefce8',borderRadius:4,border:'1px solid #fde68a'}}>
            <div><div style={{fontSize:10,color:'#92400e',fontWeight:600}}>Default Password</div><div style={{fontSize:12,fontWeight:700,color:'#1e293b',marginTop:2}}>aaaaaa12</div></div>
            <button onClick={()=>copy('aaaaaa12','Default Password')} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b',display:'flex',alignItems:'center',gap:3,fontSize:11}}>
              <Copy size={11}/>{copied==='Default Password'?'Copied!':'Copy'}
            </button>
          </div>
          {resetMsg&&<div style={{fontSize:11,padding:'6px 10px',borderRadius:4,background:resetMsg.ok?'#f0fdf4':'#fef2f2',color:resetMsg.ok?'#15803d':'#dc2626',border:`1px solid ${resetMsg.ok?'#bbf7d0':'#fecaca'}`}}>{resetMsg.text}</div>}
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button onClick={resetPwd} disabled={resetting} className="erp-btn" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontSize:11}}>
              {resetting?<Loader size={11}/>:<RefreshCw size={11}/>} Reset to aaaaaa12
            </button>
            <button onClick={()=>window.open(MBI_URL,'_blank')} className="erp-btn erp-btn-primary" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:4,fontSize:11}}>
              <ExternalLink size={11}/> Open MobileBI
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Add MobileBI User Modal ────────────────────────────────────────────────────
function MBIAddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name:'',email:'',company:'',serverIp:'',serverPort:'1433',dbName:'',dbUsername:'sa',dbPassword:'',subscriptionMonths:'12',desc3:'' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  async function save() {
    if(!form.name||!form.email||!form.serverIp||!form.dbName){setMsg({ok:false,text:'Name, Email, Server IP, DB Name are required'});return;}
    setBusy(true); setMsg(null);
    try {
      const d = await apiFetch(`${API}/client-reg/mobilbi/create-user`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(form)}).then(r=>r.json());
      if(d.ok){ setMsg({ok:true,text:`Created! Default password: ${d.defaultPassword}`}); setTimeout(()=>{onCreated();onClose();},2000); }
      else setMsg({ok:false,text:d.error||'Failed'});
    } catch(e){ setMsg({ok:false,text:e.message}); }
    finally{ setBusy(false); }
  }
  const fields = [
    ['name','Name *','text'],['email','Email *','email'],['company','Company','text'],
    ['serverIp','SQL Server IP *','text'],['serverPort','SQL Port','number'],
    ['dbName','Database Name *','text'],['dbUsername','DB Username','text'],
    ['dbPassword','DB Password','password'],['subscriptionMonths','Subscription Months','number'],['desc3','Description','text']
  ];
  return (
    <div style={{position:'fixed',inset:0,zIndex:2200,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.45)'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:480,maxHeight:'85vh',overflowY:'auto',background:'#fff',borderRadius:8,border:'1px solid #e2e8f0',boxShadow:'0 8px 32px rgba(0,0,0,0.18)'}}>
        <div style={{padding:'12px 16px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#f8fafc',position:'sticky',top:0}}>
          <div style={{fontSize:13,fontWeight:700,color:'#1e293b',display:'flex',alignItems:'center',gap:6}}><Plus size={14}/> Add MobileBI User</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b'}}><X size={14}/></button>
        </div>
        <div style={{padding:16,display:'flex',flexDirection:'column',gap:10}}>
          {fields.map(([k,l,t])=>(
            <div key={k}><label style={lbl}>{l}</label><input type={t} value={form[k]} onChange={e=>set(k,e.target.value)} style={inp} data-testid={`mbi-add-${k}`}/></div>
          ))}
          {msg&&<div style={{fontSize:11,padding:'7px 10px',borderRadius:4,background:msg.ok?'#f0fdf4':'#fef2f2',color:msg.ok?'#15803d':'#dc2626',border:`1px solid ${msg.ok?'#bbf7d0':'#fecaca'}`}}>{msg.text}</div>}
        </div>
        <div style={{padding:'10px 16px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'flex-end',gap:8,background:'#f8fafc',position:'sticky',bottom:0}}>
          <button onClick={onClose} className="erp-btn">Cancel</button>
          <button onClick={save} disabled={busy} className="erp-btn erp-btn-primary" data-testid="mbi-add-save">{busy?'Creating…':'Create User'}</button>
        </div>
      </div>
    </div>
  );
}

// ── MobileBI Tab ───────────────────────────────────────────────────────────────
function MobileBITab({ isSuperAdmin }) {
  const MBI_URL = process.env.REACT_APP_MOBILEBI_URL || 'https://veriteqcliq.in/mobileBI/';
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [loginModal, setLoginModal] = useState(null);
  const [addModal, setAddModal]   = useState(false);
  const [toggling, setToggling]   = useState({});

  async function loadClients() {
    setLoading(true); setError(null);
    try {
      const d = await apiFetch(`${API}/client-reg/mobilbi/clients`).then(r=>r.json());
      if (d.error) throw new Error(d.error);
      setClients(d.clients||[]);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(()=>{ loadClients(); },[]);

  async function toggleLock(id) {
    setToggling(t=>({...t,[id]:true}));
    try {
      await apiFetch(`${API}/client-reg/mobilbi/user/${id}/toggle-lock`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:'{}'}).then(r=>r.json());
      loadClients();
    } catch(_) {}
    finally { setToggling(t=>({...t,[id]:false})); }
  }

  async function deleteUser(id, name) {
    if (!window.confirm(`Delete MobileBI user "${name}"? This also removes their DB configuration.`)) return;
    try {
      await apiFetch(`${API}/client-reg/mobilbi/user/${id}`,{method:'DELETE'}).then(r=>r.json());
      loadClients();
    } catch(_) {}
  }

  // Dedupe: group by user id (handle multiple DB rows per user)
  const grouped = Object.values(clients.reduce((acc, row) => {
    if (!acc[row.id]) acc[row.id] = { ...row, dbs: [] };
    if (row.db_id) acc[row.id].dbs.push({ id: row.db_id, server_ip: row.server_ip, db_port: row.db_port, database_name: row.database_name, db_username: row.db_username, db_active: row.db_active, desc3: row.desc3 });
    return acc;
  }, {}));

  const subExpiry = (d) => {
    if (!d) return null;
    try { const dt = new Date(d.split('-').reverse().join('-')); return dt < new Date() ? 'expired' : 'active'; } catch { return null; }
  };

  return (
    <div style={{ padding: '12px 16px', height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <BarChart2 size={16} color="#1e3a5f"/>
          <span style={{ fontSize:13, fontWeight:700, color:'var(--erp-text)' }}>MobileBI Clients — MOBILEBIAPP_DEV</span>
          <span style={{ fontSize:11, color:'var(--erp-text-muted)', background:'#f1f5f9', padding:'2px 8px', borderRadius:12, border:'1px solid #e2e8f0' }}>
            {grouped.length} users
          </span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="erp-btn" onClick={loadClients} disabled={loading} title="Refresh" data-testid="mbi-refresh" style={{display:'flex',alignItems:'center',gap:4}}>
            {loading?<Loader size={12}/>:<RefreshCw size={12}/>} Refresh
          </button>
          <button className="erp-btn" onClick={()=>window.open(MBI_URL,'_blank')} style={{display:'flex',alignItems:'center',gap:4,fontSize:11}} data-testid="mbi-open-app">
            <ExternalLink size={12}/> Open MobileBI
          </button>
          {isSuperAdmin&&(
            <button className="erp-btn erp-btn-primary" onClick={()=>setAddModal(true)} style={{display:'flex',alignItems:'center',gap:4}} data-testid="mbi-add-btn">
              <Plus size={12}/> Add User
            </button>
          )}
        </div>
      </div>

      {error&&<div style={{padding:'10px 14px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:4,color:'#dc2626',fontSize:12,marginBottom:12}}>{error}</div>}

      {loading&&!clients.length&&(
        <div style={{textAlign:'center',padding:'40px 0',color:'var(--erp-text-muted)',fontSize:12,display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
          <Loader size={20} style={{animation:'spin 1s linear infinite'}}/> Loading MobileBI clients…
        </div>
      )}

      {!loading&&!error&&grouped.length===0&&(
        <div style={{textAlign:'center',padding:'60px 0',color:'var(--erp-text-muted)',fontSize:12}}>
          No MobileBI users found. Click <strong>Add User</strong> to create one.
        </div>
      )}

      {grouped.length>0&&(
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'var(--erp-header-bg)',borderBottom:'2px solid var(--erp-border)'}}>
              {['Status','User / Company','Email','Subscription','DB Config','Actions'].map(h=>(
                <th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:11,fontWeight:700,color:'var(--erp-text-muted)',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((c,i)=>{
              const expStatus = subExpiry(c.subscription_end);
              const isLocked  = c.is_locked;
              return (
                <tr key={c.id} data-testid={`mbi-row-${c.id}`} style={{borderBottom:'1px solid var(--erp-border)',background:i%2===0?'#fafbfc':'#fff',transition:'background .1s'}}
                  onMouseEnter={e=>e.currentTarget.style.background='#f0f4ff'}
                  onMouseLeave={e=>e.currentTarget.style.background=i%2===0?'#fafbfc':'#fff'}>
                  {/* Status */}
                  <td style={{padding:'8px 10px'}}>
                    <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'flex-start'}}>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:3,
                        background:c.account_status==='active'?'#dcfce7':'#fef2f2',
                        color:c.account_status==='active'?'#15803d':'#dc2626',
                        border:`1px solid ${c.account_status==='active'?'#bbf7d0':'#fecaca'}`}}>
                        {c.account_status||'unknown'}
                      </span>
                      {isLocked&&<span style={{fontSize:10,color:'#dc2626',fontWeight:600,display:'flex',alignItems:'center',gap:2}}><Lock size={9}/> Locked</span>}
                    </div>
                  </td>
                  {/* User */}
                  <td style={{padding:'8px 10px'}}>
                    <div style={{fontWeight:700,color:'var(--erp-text)',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={c.company||c.name}>{c.company||c.name}</div>
                    <div style={{fontSize:11,color:'var(--erp-text-muted)',marginTop:2}}>{c.name}</div>
                    {c.desc3&&<div style={{fontSize:10,color:'#64748b',marginTop:1}}>{c.desc3}</div>}
                  </td>
                  {/* Email */}
                  <td style={{padding:'8px 10px',fontSize:11,color:'#1e40af',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={c.email}>{c.email}</td>
                  {/* Subscription */}
                  <td style={{padding:'8px 10px'}}>
                    <div style={{fontSize:11}}>
                      <div style={{color:'var(--erp-text-muted)'}}>From: {c.subscription_start}</div>
                      <div style={{color:expStatus==='expired'?'#dc2626':expStatus==='active'?'#15803d':'var(--erp-text-muted)',fontWeight:600,marginTop:2}}>
                        Exp: {c.subscription_end}
                      </div>
                      {c.last_login&&<div style={{fontSize:10,color:'#64748b',marginTop:2}}>Last: {c.last_login?.slice(0,16)}</div>}
                    </div>
                  </td>
                  {/* DB Config */}
                  <td style={{padding:'8px 10px'}}>
                    {c.dbs.map((db,di)=>(
                      <div key={di} style={{fontSize:11,background:'#f1f5f9',borderRadius:3,padding:'3px 6px',marginBottom:2}}>
                        <span style={{fontFamily:'monospace',color:'#1e3a5f'}}>{db.server_ip}</span>
                        <span style={{color:'#64748b'}}>:{db.db_port}</span>
                        <span style={{color:'#334155',marginLeft:4}}>{db.database_name}</span>
                        <span style={{fontSize:10,marginLeft:4,padding:'1px 4px',borderRadius:2,background:db.db_active?'#dcfce7':'#f1f5f9',color:db.db_active?'#15803d':'#64748b'}}>{db.db_active?'Active':'Off'}</span>
                      </div>
                    ))}
                    {c.dbs.length===0&&<span style={{fontSize:11,color:'#94a3b8'}}>No DB configured</span>}
                  </td>
                  {/* Actions */}
                  <td style={{padding:'8px 10px'}}>
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      <button data-testid={`mbi-login-btn-${c.id}`} onClick={()=>setLoginModal(c)} className="erp-btn erp-btn-primary" style={{fontSize:10,padding:'3px 8px',display:'flex',alignItems:'center',gap:3}}>
                        <ExternalLink size={10}/> Login
                      </button>
                      {isSuperAdmin&&(
                        <>
                          <button data-testid={`mbi-lock-btn-${c.id}`} onClick={()=>toggleLock(c.id)} disabled={toggling[c.id]} className="erp-btn" style={{fontSize:10,padding:'3px 8px',display:'flex',alignItems:'center',gap:3}}>
                            {toggling[c.id]?<Loader size={9}/>:isLocked?<Unlock size={9}/>:<Lock size={9}/>}
                            {isLocked?'Unlock':'Lock'}
                          </button>
                          <button data-testid={`mbi-del-btn-${c.id}`} onClick={()=>deleteUser(c.id,c.name)} className="erp-btn" style={{fontSize:10,padding:'3px 8px',color:'#dc2626',display:'flex',alignItems:'center',gap:3}}>
                            <Trash2 size={9}/> Del
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {loginModal&&<MBILoginModal client={loginModal} onClose={()=>setLoginModal(null)} onResetPwd={loadClients}/>}
      {addModal&&<MBIAddUserModal onClose={()=>setAddModal(false)} onCreated={loadClients}/>}
    </div>
  );
}

export default function ClientRegistrationPage({ isSuperAdmin }) {
  const [activeTab, setActiveTab]   = useState('leads');
  const [modules, setModules]       = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [industryMap, setIndustryMap] = useState({});   // code → name
  const [counts, setCounts]           = useState(null);  // {totalLeads,registeredCount,pendingCount}
  const [pingCache, setPingCache]     = useState({});
  const [countries, setCountries]     = useState([]);    // [{CountryCode, CountryName}]

  // Leads grid state
  const [leads, setLeads]         = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadPage, setLeadPage]   = useState(1);
  const [leadSize, setLeadSize]   = useState(50);
  const [leadFilters, setLeadFilters] = useState({});
  const [leadLoading, setLeadLoading] = useState(false);

  // Clients grid state
  const [clients, setClients]     = useState([]);
  const [cliTotal, setCliTotal]   = useState(0);
  const [cliPage, setCliPage]     = useState(1);
  const [cliSize, setCliSize]     = useState(50);
  const [cliFilters, setCliFilters] = useState({});
  const [cliLoading, setCliLoading] = useState(false);

  // Column deletions (persistent via localStorage)
  const [deletedLeadCols, setDeletedLeadCols]     = useState(() => getDeletedCols(LEAD_COLS_KEY));
  const [deletedClientCols, setDeletedClientCols] = useState(() => getDeletedCols(CLIENT_COLS_KEY));

  // Modals
  const [wizard, setWizard]           = useState(null);
  const [connModal, setConnModal]     = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const refresh = () => setRefreshKey(k => k + 1);

  const leadColumns   = ALL_LEAD_COLUMNS.filter(c => !deletedLeadCols.has(c.field));
  const clientColumns = ALL_CLIENT_COLUMNS.filter(c => !deletedClientCols.has(c.field));
  const leadSearch    = Object.values(leadFilters).filter(Boolean).join(' ');
  const clientSearch  = Object.values(cliFilters).filter(Boolean).join(' ');

  const loadLeadsRef = useRef();
  loadLeadsRef.current = async () => {
    setLeadLoading(true);
    try{
      const params = new URLSearchParams({page:leadPage,pageSize:leadSize});
      if(leadSearch) params.set('search',leadSearch);
      const d = await apiFetch(`${API}/client-reg/leads?${params}`).then(r=>r.json());
      setLeads(d.leads||[]);setLeadTotal(d.total||0);
    }catch{setLeads([]);setLeadTotal(0);}
    finally{setLeadLoading(false);}
  };

  const loadClientsRef = useRef();
  loadClientsRef.current = async () => {
    setCliLoading(true);
    try{
      const params = new URLSearchParams({page:cliPage,pageSize:cliSize});
      if(clientSearch) params.set('search',clientSearch);
      const d = await apiFetch(`${API}/client-reg/clients?${params}`).then(r=>r.json());
      setClients(d.clients||[]);setCliTotal(d.total||0);
    }catch{setClients([]);setCliTotal(0);}
    finally{setCliLoading(false);}
  };

  useEffect(()=>{
    apiFetch(`${API}/client-reg/modules`).then(r=>r.json()).then(d=>setModules(d.modules||[])).catch(()=>{});
    apiFetch(`${API}/uams/industries`).then(r=>r.json()).then(list=>{
      if(Array.isArray(list)){const m={};list.forEach(i=>{m[i.IndustryCode]=i.IndustryName||i.IndustryCode;});setIndustryMap(m);}
    }).catch(()=>{});
    apiFetch(`${API}/client-reg/counts`).then(r=>r.json()).then(d=>setCounts(d)).catch(()=>{});
    apiFetch(`${API}/country-fiscal`).then(r=>r.json()).then(list=>{if(Array.isArray(list))setCountries(list);}).catch(()=>{});
  },[]);
  useEffect(()=>{ if(activeTab==='leads') loadLeadsRef.current(); },[activeTab,leadPage,leadSize,leadSearch,refreshKey]);
  useEffect(()=>{ if(activeTab==='clients') loadClientsRef.current(); },[activeTab,cliPage,cliSize,clientSearch,refreshKey]);

  function handleDeleteLeadCol(field){
    const s=new Set([...deletedLeadCols,field]);setDeletedLeadCols(s);saveDeletedCols(LEAD_COLS_KEY,s);
  }
  function handleDeleteClientCol(field){
    const s=new Set([...deletedClientCols,field]);setDeletedClientCols(s);saveDeletedCols(CLIENT_COLS_KEY,s);
  }

  const leadCellRenderer = useCallback((field, value, row) => {
    if(field==='alreadyRegistered') return(
      <button data-testid="lead-register-btn" onClick={e=>{e.stopPropagation();if(!value)setWizard({initial:row});}} disabled={value}
        style={{padding:'3px 10px',borderRadius:4,border:'none',cursor:value?'default':'pointer',background:value?'#dcfce7':'var(--erp-primary)',color:value?'#15803d':'#fff',fontSize:11,fontWeight:700,whiteSpace:'nowrap'}}>
        {value?'Registered':'+ Register'}
      </button>
    );
    if(field==='industryCode'&&value){
      const name=industryMap[value];
      return<span title={value} style={{fontSize:11}}>{name||value}</span>;
    }
    if(field==='expiryDate'&&value){
      let isPast=false;
      try{const p=String(value).match(/(\d{2})-([A-Za-z]{3})-(\d{4})/);if(p){const d=new Date(`${p[2]} ${p[1]}, ${p[3]}`);isPast=d<new Date();};}catch(_){}
      return<span style={{color:isPast?'#dc2626':'green',fontWeight:600,fontSize:11}}>{value}</span>;
    }
    return null;
  },[industryMap]);

  const [mbiLoginPopover, setMbiLoginPopover] = useState(null); // { clientId, email, company, anchorRect }
  const [mbiLockBusy, setMbiLockBusy] = useState({});

  // ── MBI row expiry style ─────────────────────────────────────────────────
  const clientRowStyle = useCallback((row) => {
    if (row.ClientType !== 'MOBILEBI') return {};
    const sub = row.mbiSubEnd; // dd-mm-yyyy
    if (!sub) return {};
    try {
      const [d, m, y] = sub.split('-');
      const expDate = new Date(`${y}-${m}-${d}`);
      const daysLeft = Math.floor((expDate - new Date()) / 86400000);
      if (daysLeft < 0)  return { background: '#fef2f2', borderLeft: '3px solid #f87171' }; // expired — red
      if (daysLeft <= 30) return { background: '#fffbeb', borderLeft: '3px solid #f59e0b' }; // near — amber
    } catch (_) {}
    return {};
  }, []);

  // ── lock/unlock MBI user directly from grid ──────────────────────────────
  const toggleMbiLock = useCallback(async (mbiUserId, currentLocked) => {
    setMbiLockBusy(b => ({ ...b, [mbiUserId]: true }));
    try {
      await apiFetch(`${API}/client-reg/mobilbi/user/${mbiUserId}/toggle-lock`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: '{}'
      });
      refresh(); // reload the clients grid
    } catch (_) {}
    finally { setMbiLockBusy(b => ({ ...b, [mbiUserId]: false })); }
  }, []);

  const clientCellRenderer = useCallback((field, value, row) => {
    if(field==='sqlStatus') return <SqlPingDot row={row} pingCache={pingCache} setPingCache={setPingCache}/>;
    if(field==='mbiRegister') {
      // ERP row registered from MBI — show ERP credentials button
      if(row.RegSource==='MOBILEBI' && row.ClientType!=='MOBILEBI') {
        const suffix = row.ClientId.replace(/[^0-9]/g,'').slice(-4);
        return (
          <button data-testid={`erp-creds-btn-${row.ClientId}`}
            onClick={e=>{e.stopPropagation();const rect=e.currentTarget.getBoundingClientRect();setMbiLoginPopover({
              isErpCreds:true, clientId:row.ClientId, company:row.CompanyName,
              adminUser:`admin${suffix}`, adminPass:'aaaaaa12',
              licUser:`SysCLIQ${suffix}`, licPass:'aaaaaa99',
              ssUser:`ssadmin${suffix}`, ssPass:'aaaaaa12', rect
            });}}
            style={{padding:'3px 8px',borderRadius:4,border:'1px solid #c7d2fe',cursor:'pointer',background:'#eef2ff',color:'#4338ca',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',gap:2}}>
            <Database size={9}/> ERP Credentials
          </button>
        );
      }
      if(row.ClientType!=='MOBILEBI') return null;
      const isLocked  = row.mbiIsLocked;
      const mbiUserId = row.mbiUserId;
      const subStr    = row.mbiSubEnd;
      let daysLeft = null, expiryBadge = null;
      if (subStr) {
        try {
          const [d,m,y] = subStr.split('-');
          daysLeft = Math.floor((new Date(`${y}-${m}-${d}`) - new Date()) / 86400000);
          if (daysLeft < 0)  expiryBadge = <span style={{fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:3,background:'#fef2f2',color:'#dc2626',border:'1px solid #fecaca'}}>Expired</span>;
          else if (daysLeft <= 30) expiryBadge = <span style={{fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:3,background:'#fffbeb',color:'#d97706',border:'1px solid #fde68a'}}>{daysLeft}d left</span>;
        } catch(_) {}
      }
      const showRenew = daysLeft !== null && daysLeft <= 30;
      return (
        <div style={{display:'flex',gap:3,alignItems:'center',flexWrap:'wrap'}}>
          <button data-testid={`mbi-reg-btn-${mbiUserId}`}
            onClick={e=>{e.stopPropagation();setWizard({initial:{
              companyName:row.CompanyName, email:row.EmailId,
              onPremServer:row.OnPremSQLServer, onPremPort:String(row.OnPremSQLPort||'6558'),
              onPremDB:row.OnPremSQLDB, onPremSQLUser:row.OnPremSQLUser||'sa',
              leadSourceId:row.LeadSourceId, clientType:'HYBRID', serviceType:'ERP+BI',
              serviceURLsRaw:JSON.stringify({BI:'https://veriteqcliq.in/mobileBI/'}),
            }});}}
            style={{padding:'3px 7px',borderRadius:4,border:'none',cursor:'pointer',background:'#1e3a5f',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',gap:2}}>
            <Plus size={9}/> Register
          </button>
          {expiryBadge}
          {showRenew && (
            <button data-testid={`mbi-renew-btn-${mbiUserId}`}
              disabled={!!mbiLockBusy[`renew_${mbiUserId}`]}
              onClick={async e=>{e.stopPropagation();setMbiLockBusy(b=>({...b,[`renew_${mbiUserId}`]:true}));
                try{const d=await apiFetch(`${API}/client-reg/mobilbi/user/${mbiUserId}/renew`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:'{}'}).then(r=>r.json());
                  if(d.ok){alert(`Renewed! New expiry: ${d.newEnd}`);refresh();}else alert(d.error||'Failed');
                }catch(err){alert(err.message);}
                finally{setMbiLockBusy(b=>({...b,[`renew_${mbiUserId}`]:false}));}}}
              style={{padding:'3px 7px',borderRadius:4,border:'1px solid #fde68a',cursor:'pointer',background:'#fefce8',color:'#d97706',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',gap:2}}>
              {mbiLockBusy[`renew_${mbiUserId}`]?<Loader size={9}/>:<RefreshCw size={9}/>} +1yr
            </button>
          )}
          <button data-testid={`mbi-open-btn-${row.ClientId}`}
            onClick={e=>{e.stopPropagation();const rect=e.currentTarget.getBoundingClientRect();setMbiLoginPopover({clientId:row.ClientId,email:row.EmailId,company:row.CompanyName,rect});}}
            style={{padding:'3px 7px',borderRadius:4,border:'1px solid #bfdbfe',cursor:'pointer',background:'#eff6ff',color:'#1d4ed8',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',gap:2}}>
            <ExternalLink size={9}/> MobileBI
          </button>
          <button data-testid={`mbi-lock-btn-${mbiUserId}`}
            disabled={!!mbiLockBusy[mbiUserId]}
            onClick={e=>{e.stopPropagation();toggleMbiLock(mbiUserId,isLocked);}}
            style={{padding:'3px 7px',borderRadius:4,border:`1px solid ${isLocked?'#fca5a5':'#d1d5db'}`,cursor:'pointer',background:isLocked?'#fef2f2':'#f9fafb',color:isLocked?'#dc2626':'#374151',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',gap:2}}>
            {mbiLockBusy[mbiUserId]?<Loader size={9}/>:isLocked?<Unlock size={9}/>:<Lock size={9}/>}
            {isLocked?'Unlock':'Lock'}
          </button>
          <button data-testid={`mbi-reset-btn-${mbiUserId}`}
            disabled={!!mbiLockBusy[`reset_${mbiUserId}`]}
            onClick={async e=>{e.stopPropagation();if(!window.confirm(`Reset MobileBI password for "${row.CompanyName}" to "aaaaaa12"?`))return;
              setMbiLockBusy(b=>({...b,[`reset_${mbiUserId}`]:true}));
              try{const d=await apiFetch(`${API}/client-reg/mobilbi/user/${mbiUserId}/reset-password`,{method:'PATCH',headers:{'Content-Type':'application/json'},body:JSON.stringify({password:'aaaaaa12'})}).then(r=>r.json());
                if(d.ok)alert(`Password reset to: ${d.newPassword}`);else alert(d.error||'Failed');
              }catch(err){alert(err.message);}
              finally{setMbiLockBusy(b=>({...b,[`reset_${mbiUserId}`]:false}));}}}
            style={{padding:'3px 7px',borderRadius:4,border:'1px solid #e2e8f0',cursor:'pointer',background:'#f8fafc',color:'#64748b',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',gap:2}}>
            {mbiLockBusy[`reset_${mbiUserId}`]?<Loader size={9}/>:<RefreshCw size={9}/>} Pwd
          </button>
        </div>
      );
    }
    if(field==='Status') return<span style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:3,background:value==='Active'?'#dcfce7':value==='Locked'?'#fef2f2':'#f1f5f9',color:value==='Active'?'#15803d':value==='Locked'?'#dc2626':'#64748b',border:`1px solid ${value==='Active'?'#bbf7d0':value==='Locked'?'#fecaca':'#e2e8f0'}`}}>{value}</span>;
    if(field==='ClientType') {
      const isMBI = value==='MOBILEBI';
      return<span style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:3,background:isMBI?'#eff6ff':'#fef3c7',color:isMBI?'#1d4ed8':'#92400e',border:`1px solid ${isMBI?'#bfdbfe':'#fde68a'}`}}>{isMBI?'MobileBI':value}</span>;
    }
    if(field==='ServiceType') return<span style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:3,background:'#ede9fe',color:'#5b21b6',border:'1px solid #ddd6fe'}}>{value}</span>;
    return null;
  },[pingCache,setPingCache,mbiLockBusy,toggleMbiLock]);

  async function deleteClient(id,name){
    if(!window.confirm(`Deactivate ${name} (${id})?`))return;
    await apiFetch(`${API}/client-reg/client/${id}`,{method:'DELETE'});
    refresh();
  }

  const onLeadEdit  = useCallback((row)=>{if(!row.alreadyRegistered)setWizard({initial:row});},[]);
  const onCliEdit   = useCallback((row)=>{if(isSuperAdmin)setConnModal(row);},[isSuperAdmin]);

  const pendingBadge = counts ? ` (${counts.pendingCount} pending)` : '';
  const totalBadge   = counts ? ` (${counts.registeredCount} ERP · ${counts.mbiCount ?? '…'} MBI)` : '';

  return (
    <div className="erp-module" style={{height:'100%',borderRadius:0,border:'none'}}>
      {/* Tabs */}
      <div className="erp-screen-header" style={{justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          {[{id:'leads',label:`Lead Browser${pendingBadge}`},{id:'clients',label:`Registered Clients${totalBadge}`},{id:'connectivity',label:'SQL Connectivity'}].map(t=>(
            <button key={t.id} data-testid={`cr-tab-${t.id}`} onClick={()=>setActiveTab(t.id)}
              style={{padding:'6px 16px',fontSize:12,fontWeight:700,background:'none',border:'none',cursor:'pointer',borderBottom:activeTab===t.id?'2px solid var(--erp-primary)':'2px solid transparent',color:activeTab===t.id?'var(--erp-primary)':'var(--erp-text-muted)'}}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="erp-screen-actions" style={{display:'flex',gap:6,alignItems:'center'}}>
          <button onClick={refresh} className="erp-btn" title="Refresh" data-testid="cr-refresh">↺</button>
          {isSuperAdmin&&(
            <button className="erp-btn" data-testid="cr-settings" title="Lead DB Settings" onClick={()=>setShowSettings(true)} style={{display:'flex',alignItems:'center',gap:4}}>
              <Settings size={13}/>
            </button>
          )}
        </div>
      </div>

      {/* Leads Grid */}
      {activeTab==='leads'&&(
        <ERPAdvancedGrid
          title="Qualified Leads (LeadStatus=6)"
          columns={leadColumns}
          data={leads}
          total={leadTotal}
          idField="leadId" nameField="companyName"
          page={leadPage} pageSize={leadSize}
          onPageChange={p=>setLeadPage(p)}
          onPageSizeChange={s=>{setLeadSize(s);setLeadPage(1);}}
          pageSizeOptions={[20,50,100,200,500,1000]}
          sortField={null} sortDir="asc" onSort={()=>{}}
          filters={leadFilters} onFilterChange={f=>{setLeadFilters(f);setLeadPage(1);}}
          loading={leadLoading}
          showAddNew={true}
          onAddNew={()=>setWizard({initial:{}})}
          onEdit={onLeadEdit}
          onRowClick={row=>{if(!row.alreadyRegistered)setWizard({initial:row});}}
          testId="leads-grid"
          cellRenderer={leadCellRenderer}
          exportFileName="qualified-leads"
          onDeleteColumn={handleDeleteLeadCol}
          storageKey="cr_leads"
          headerActions={<span style={{fontSize:11,color:'var(--erp-text-muted)'}}>{leadTotal} leads · click row to register</span>}
        />
      )}

      {/* Clients Grid */}
      {activeTab==='clients'&&(
        <ERPAdvancedGrid
          title="Registered Clients (On-Prem / Hybrid)"
          columns={clientColumns}
          data={clients}
          total={cliTotal}
          idField="ClientId" nameField="CompanyName"
          page={cliPage} pageSize={cliSize}
          onPageChange={p=>setCliPage(p)}
          onPageSizeChange={s=>{setCliSize(s);setCliPage(1);}}
          pageSizeOptions={[20,50,100,200,500,1000]}
          sortField={null} sortDir="asc" onSort={()=>{}}
          filters={cliFilters} onFilterChange={f=>{setCliFilters(f);setCliPage(1);}}
          loading={cliLoading}
          showAddNew={false}
          onEdit={onCliEdit}
          onVoid={isSuperAdmin?(row)=>deleteClient(row.ClientId,row.CompanyName):undefined}
          testId="clients-grid"
          cellRenderer={clientCellRenderer}
          exportFileName="registered-clients"
          onDeleteColumn={handleDeleteClientCol}
          storageKey="cr_clients"
          rowStyle={clientRowStyle}
        />
      )}

      {activeTab==='connectivity'&&<SqlConnectivityTab/>}
      {/* MobileBI tab removed — MBI clients now merged into Registered Clients */}

      {wizard&&<RegistrationWizard initial={wizard.initial} modules={modules} isSuperAdmin={isSuperAdmin} countries={countries} onClose={()=>setWizard(null)} onDone={()=>{setWizard(null);setActiveTab('clients');refresh();setCounts(null);apiFetch(`${API}/client-reg/counts`).then(r=>r.json()).then(d=>setCounts(d)).catch(()=>{});}}/>}
      {connModal&&<ConnectionEditModal client={connModal} onClose={()=>setConnModal(null)} onSaved={()=>{setConnModal(null);refresh();}}/>}
      {showSettings&&<LeadDBSettingsModal onClose={()=>{setShowSettings(false);refresh();}}/>}

      {/* MobileBI login / ERP credentials popover */}
      {mbiLoginPopover&&(
        <div style={{position:'fixed',inset:0,zIndex:3000}} onClick={()=>setMbiLoginPopover(null)}>
          <div onClick={e=>e.stopPropagation()} style={{
            position:'fixed',
            top: Math.min((mbiLoginPopover.rect?.bottom||200)+8, window.innerHeight-260),
            left: Math.min((mbiLoginPopover.rect?.left||200), window.innerWidth-320),
            width:310, background:'#fff', borderRadius:8,
            border:'1px solid #bfdbfe', boxShadow:'0 8px 24px rgba(0,0,0,0.15)',
            overflow:'hidden', zIndex:3001
          }}>
            {mbiLoginPopover.isErpCreds ? (
              <>
                <div style={{padding:'9px 12px',background:'#312e81',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><Database size={13} color="#a5b4fc"/>
                    <span style={{fontSize:12,fontWeight:700,color:'#fff'}}>ERP Credentials — {mbiLoginPopover.company}</span>
                  </div>
                  <button onClick={()=>setMbiLoginPopover(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',lineHeight:1}}><X size={12}/></button>
                </div>
                <div style={{padding:12,display:'flex',flexDirection:'column',gap:6}}>
                  <div style={{fontSize:10,color:'#64748b',padding:'4px 8px',background:'#f8fafc',borderRadius:3,border:'1px solid #e2e8f0'}}>Client ID: <strong>{mbiLoginPopover.clientId}</strong></div>
                  {[['Admin',mbiLoginPopover.adminUser,mbiLoginPopover.adminPass],
                    ['Licensor',mbiLoginPopover.licUser,mbiLoginPopover.licPass],
                    ['SS Admin',mbiLoginPopover.ssUser,mbiLoginPopover.ssPass]].map(([role,usr,pwd])=>(
                    <div key={role} style={{background:'#f8fafc',borderRadius:4,border:'1px solid #e2e8f0',padding:'6px 8px'}}>
                      <div style={{fontSize:10,color:'#64748b',fontWeight:700,marginBottom:3}}>{role}</div>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
                        <span style={{fontSize:11,fontFamily:'monospace',color:'#1e293b'}}>{usr}</span>
                        <button onClick={()=>navigator.clipboard.writeText(usr).catch(()=>{})} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b',fontSize:10,display:'flex',alignItems:'center',gap:1}}><Copy size={9}/></button>
                      </div>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <span style={{fontSize:11,fontFamily:'monospace',color:'#374151'}}>{pwd}</span>
                        <button onClick={()=>navigator.clipboard.writeText(pwd).catch(()=>{})} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b',fontSize:10,display:'flex',alignItems:'center',gap:1}}><Copy size={9}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div style={{padding:'9px 12px',background:'#1e3a5f',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6}}><BarChart2 size={13} color="#60a5fa"/>
                    <span style={{fontSize:12,fontWeight:700,color:'#fff'}}>MobileBI — {mbiLoginPopover.company}</span>
                  </div>
                  <button onClick={()=>setMbiLoginPopover(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',lineHeight:1}}><X size={12}/></button>
                </div>
                <div style={{padding:12,display:'flex',flexDirection:'column',gap:8}}>
                  {[['Email',mbiLoginPopover.email],['Password','aaaaaa12']].map(([lbl,val])=>(
                    <div key={lbl} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 8px',background:'#f8fafc',borderRadius:4,border:'1px solid #e2e8f0'}}>
                      <div>
                        <div style={{fontSize:10,color:'#64748b',fontWeight:600}}>{lbl}</div>
                        <div style={{fontSize:12,fontWeight:700,color:'#1e293b',marginTop:1,fontFamily:lbl==='Password'?'monospace':'inherit'}}>{val}</div>
                      </div>
                      <button onClick={()=>navigator.clipboard.writeText(val).catch(()=>{})}
                        style={{background:'none',border:'none',cursor:'pointer',color:'#64748b',display:'flex',alignItems:'center',gap:2,fontSize:10}}>
                        <Copy size={10}/> Copy
                      </button>
                    </div>
                  ))}
                  <button onClick={()=>{window.open('https://veriteqcliq.in/mobileBI/','_blank');setMbiLoginPopover(null);}}
                    data-testid="mbi-open-url-btn"
                    style={{width:'100%',padding:'7px 12px',borderRadius:5,border:'none',cursor:'pointer',background:'#1d4ed8',color:'#fff',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:5}}>
                    <ExternalLink size={12}/> Open MobileBI Reports
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
